import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, kaspreadsheetUrl } from '@/mocks/categories';
import { features, spreadsheetComparison, howItWorks } from '@/mocks/features';
import { seoArticles } from '@/mocks/articles';

function CategoryDropdown() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (cat: typeof categories[0]) => {
    setSelected(cat.name);
    setOpen(false);
    window.open(cat.url, '_blank');
  };

  return (
    <div className="relative w-full max-w-md mx-auto" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 rounded-full border-2 border-white/40 bg-white/20 backdrop-blur-sm text-white text-sm font-medium hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
      >
        <span className="flex items-center gap-2">
          <i className="ri-store-2-line w-4 h-4 flex items-center justify-center"></i>
          {selected || 'Browse All Categories'}
        </span>
        <i className={`w-4 h-4 flex items-center justify-center transition-transform ${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
      </button>
      {open && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-lg py-2 max-h-64 overflow-y-auto z-50">
          {categories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => handleSelect(cat)}
              className="w-full text-left px-5 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const spreadsheetSystems = [
  {
    name: 'GTBuy Spreadsheet',
    description: 'High automation, business-ready workflows, intuitive interface',
    image: 'https://readdy.ai/api/search-image?query=Modern%20bright%20workspace%20with%20laptop%20showing%20sleek%20colorful%20dashboard%20interface%20graphs%20and%20charts%20on%20screen%20organized%20desk%20with%20succulent%20plant%20warm%20natural%20window%20lighting%20clean%20minimal%20aesthetic%20professional%20tech%20photography%20cream%20walls%20soft%20shadows%20inviting%20atmosphere&width=800&height=600&seq=system-gtbuy-fresh&orientation=landscape',
  },
  {
    name: 'Kakobuy Spreadsheet',
    description: 'Beginner-friendly, guided setup, gentle learning curve',
    image: 'https://readdy.ai/api/search-image?query=Cozy%20home%20office%20with%20laptop%20displaying%20simple%20clean%20spreadsheet%20interface%20on%20screen%20coffee%20cup%20and%20notebook%20on%20wooden%20desk%20soft%20morning%20light%20through%20curtain%20Scandinavian%20style%20interior%20warm%20beige%20tones%20organized%20clutter%20free%20space%20editorial%20photography%20calm%20productive%20atmosphere&width=800&height=600&seq=system-kakobuy-fresh&orientation=landscape',
  },
  {
    name: 'Superbuy Spreadsheet',
    description: 'Advanced features, custom formulas, deep integrations',
    image: 'https://readdy.ai/api/search-image?query=Professional%20dual%20screen%20workstation%20showing%20complex%20data%20analytics%20and%20spreadsheet%20visualizations%20dark%20wood%20desk%20modern%20office%20environment%20warm%20desk%20lamp%20lighting%20mechanical%20keyboard%20organized%20cables%20high%20tech%20aesthetic%20editorial%20photography%20sophisticated%20power%20user%20setup&width=800&height=600&seq=system-superbuy-fresh&orientation=landscape',
  },
];

const categoryImages: Record<string, string> = {
  'Shoes': 'https://readdy.ai/api/search-image?query=Stylish%20premium%20sneakers%20arranged%20artistically%20on%20warm%20terracotta%20toned%20surface%20soft%20diffused%20overhead%20lighting%20modern%20fashion%20editorial%20composition%20clean%20minimalist%20product%20display%20warm%20earthy%20background%20gentle%20natural%20shadows%20high%20detail%20textile%20texture%20visible&width=800&height=800&seq=cat-shoes-v2&orientation=squarish',
  'Hoodies/Sweaters': 'https://readdy.ai/api/search-image?query=Cozy%20premium%20hoodie%20neatly%20folded%20on%20warm%20sand%20colored%20linen%20surface%20soft%20natural%20window%20light%20casual%20fashion%20editorial%20style%20clean%20minimal%20product%20photography%20warm%20neutral%20background%20fabric%20texture%20visible%20relaxed%20comfortable%20aesthetic&width=600&height=600&seq=cat-hoodies-v2&orientation=squarish',
  'T-Shirts': 'https://readdy.ai/api/search-image?query=Premium%20cotton%20tshirts%20in%20pastel%20tones%20neatly%20stacked%20on%20warm%20cream%20marble%20counter%20soft%20morning%20light%20streaming%20through%20window%20modern%20apparel%20editorial%20photography%20clean%20fresh%20aesthetic%20gentle%20shadows%20minimal%20product%20display&width=600&height=600&seq=cat-tshirts-v2&orientation=squarish',
  'Jackets': 'https://readdy.ai/api/search-image?query=Stylish%20modern%20jacket%20displayed%20on%20wooden%20hanger%20against%20warm%20oatmeal%20colored%20wall%20soft%20natural%20lighting%20boutique%20fashion%20editorial%20style%20clean%20composition%20high%20detail%20fabric%20texture%20visible%20sophisticated%20casual%20outerwear%20aesthetic&width=600&height=600&seq=cat-jackets-v2&orientation=squarish',
  'Pants/Shorts': 'https://readdy.ai/api/search-image?query=Well%20tailored%20casual%20pants%20and%20shorts%20neatly%20folded%20on%20warm%20beige%20linen%20surface%20soft%20diffused%20lighting%20modern%20apparel%20editorial%20photography%20clean%20minimal%20display%20warm%20neutral%20tones%20fabric%20detail%20visible%20organized%20composition&width=600&height=600&seq=cat-pants-v2&orientation=squarish',
  'Headwear': 'https://readdy.ai/api/search-image?query=Collection%20of%20stylish%20caps%20and%20beanies%20arranged%20on%20warm%20cream%20surface%20various%20colors%20and%20textures%20soft%20natural%20lighting%20modern%20accessory%20editorial%20photography%20clean%20minimalist%20display%20warm%20neutral%20background%20gentle%20shadows&width=600&height=600&seq=cat-headwear-v2&orientation=squarish',
  'Sets': 'https://readdy.ai/api/search-image?query=Coordinated%20fashion%20clothing%20sets%20displayed%20together%20on%20warm%20sand%20toned%20surface%20matching%20tops%20and%20bottoms%20neatly%20arranged%20soft%20natural%20lighting%20editorial%20product%20photography%20clean%20aesthetic%20harmonious%20color%20palette%20warm%20background&width=600&height=600&seq=cat-sets-v2&orientation=squarish',
  'Underwear/Underpants': 'https://readdy.ai/api/search-image?query=Premium%20underwear%20essentials%20neatly%20folded%20on%20warm%20cream%20linen%20surface%20soft%20diffused%20lighting%20discreet%20elegant%20product%20photography%20clean%20minimal%20composition%20warm%20neutral%20background%20refined%20simple%20aesthetic&width=600&height=600&seq=cat-underwear-v2&orientation=squarish',
  'Jersey': 'https://readdy.ai/api/search-image?query=Sports%20jersey%20displayed%20flat%20on%20warm%20beige%20textured%20surface%20athletic%20fabric%20texture%20visible%20soft%20natural%20lighting%20sportswear%20editorial%20photography%20clean%20composition%20vibrant%20yet%20muted%20color%20palette%20warm%20neutral%20background&width=600&height=600&seq=cat-jersey-v2&orientation=squarish',
  'Accessories': 'https://readdy.ai/api/search-image?query=Elegant%20fashion%20accessories%20collection%20watches%20bracelets%20and%20sunglasses%20arranged%20on%20warm%20cream%20marble%20surface%20soft%20natural%20window%20light%20editorial%20product%20photography%20clean%20minimalist%20display%20warm%20neutral%20tones%20sophisticated%20aesthetic%20gentle%20reflections&width=600&height=600&seq=cat-accessories-v2&orientation=squarish',
  'Bags': 'https://readdy.ai/api/search-image?query=Stylish%20handbags%20and%20backpacks%20arranged%20on%20warm%20sand%20colored%20surface%20various%20sizes%20and%20textures%20soft%20natural%20lighting%20fashion%20accessory%20editorial%20photography%20clean%20modern%20composition%20warm%20neutral%20background%20leather%20details%20visible&width=600&height=600&seq=cat-bags-v2&orientation=squarish',
  'Belts': 'https://readdy.ai/api/search-image?query=Premium%20leather%20belts%20displayed%20elegantly%20on%20warm%20cream%20surface%20metallic%20buckles%20catching%20soft%20light%20fashion%20accessory%20editorial%20photography%20clean%20minimal%20composition%20warm%20neutral%20background%20refined%20sophisticated%20aesthetic&width=600&height=600&seq=cat-belts-v2&orientation=squarish',
  'Perfumes': 'https://readdy.ai/api/search-image?query=Luxury%20perfume%20bottles%20arranged%20on%20warm%20marble%20surface%20with%20soft%20golden%20hour%20light%20gentle%20reflections%20elegant%20fragrance%20editorial%20photography%20clean%20sophisticated%20composition%20warm%20neutral%20background%20crystal%20glass%20details%20visible&width=600&height=600&seq=cat-perfumes-v2&orientation=squarish',
  'Other Stuff': 'https://readdy.ai/api/search-image?query=Miscellaneous%20fashion%20and%20lifestyle%20products%20arranged%20on%20warm%20cream%20surface%20including%20small%20accessories%20gadgets%20and%20unique%20items%20soft%20natural%20lighting%20eclectic%20editorial%20photography%20clean%20composition%20warm%20neutral%20background%20diverse%20objects%20harmonious%20arrangement&width=600&height=600&seq=cat-other-v2&orientation=squarish',
};

const bannerProducts = [
  { name: 'Shoes', image: 'https://readdy.ai/api/search-image?query=Single%20premium%20designer%20sneaker%20isolated%20on%20warm%20terracotta%20cream%20gradient%20background%20minimalist%20product%20photography%20clean%20studio%20lighting%20elegant%20simplicity%20high%20detail&width=200&height=200&seq=banner-shoes-v2&orientation=squarish' },
  { name: 'Bag', image: 'https://readdy.ai/api/search-image?query=Single%20elegant%20leather%20handbag%20isolated%20on%20warm%20sand%20cream%20gradient%20background%20minimalist%20product%20photography%20clean%20studio%20lighting%20refined%20simplicity%20high%20detail&width=200&height=200&seq=banner-bag-v2&orientation=squarish' },
  { name: 'Belt', image: 'https://readdy.ai/api/search-image?query=Single%20premium%20leather%20belt%20coiled%20elegantly%20isolated%20on%20warm%20beige%20cream%20gradient%20background%20minimalist%20product%20photography%20clean%20studio%20lighting%20sophisticated%20simplicity&width=200&height=200&seq=banner-belt-v2&orientation=squarish' },
  { name: 'Perfume', image: 'https://readdy.ai/api/search-image?query=Single%20luxury%20crystal%20perfume%20bottle%20isolated%20on%20warm%20oatmeal%20cream%20gradient%20background%20minimalist%20product%20photography%20clean%20studio%20lighting%20elegant%20reflections%20high%20detail&width=200&height=200&seq=banner-perfume-v2&orientation=squarish' },
  { name: 'T-Shirt', image: 'https://readdy.ai/api/search-image?query=Single%20premium%20cotton%20tshirt%20neatly%20folded%20isolated%20on%20warm%20cream%20gradient%20background%20minimalist%20product%20photography%20clean%20studio%20lighting%20soft%20fabric%20texture%20visible&width=200&height=200&seq=banner-tshirt-v2&orientation=squarish' },
  { name: 'Cap', image: 'https://readdy.ai/api/search-image?query=Single%20stylish%20baseball%20cap%20isolated%20on%20warm%20sand%20cream%20gradient%20background%20minimalist%20product%20photography%20clean%20studio%20lighting%20casual%20elegance%20high%20detail%20stitching%20visible&width=200&height=200&seq=banner-cap-v2&orientation=squarish' },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url("https://readdy.ai/api/search-image?query=Artistic%20fashion%20flat%20lay%20composition%20with%20sneakers%20hoodies%20caps%20bags%20and%20accessories%20arranged%20in%20creative%20geometric%20pattern%20on%20warm%20cream%20textured%20surface%20soft%20diffused%20morning%20light%20from%20large%20window%20clean%20modern%20aesthetic%20editorial%20product%20photography%20warm%20neutral%20tones%20gentle%20shadows%20harmonious%20color%20palette%20high%20detail&width=1600&height=900&seq=hero-bg-v2-fresh&orientation=landscape")`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />

          <div className="relative z-10 w-full text-center px-4">
            <div className="max-w-4xl mx-auto">
              <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight">
                <span className="block">GTBuy</span>
                <span className="block font-heading italic font-normal text-3xl md:text-5xl lg:text-6xl">Spreadsheet</span>
              </h1>
              <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto mb-8 font-light tracking-wide">
                Track, Manage &amp; Optimize Your Shopping Workflow
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href={kaspreadsheetUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-8 py-3.5 rounded-full bg-white text-foreground-950 text-sm font-semibold hover:bg-white/90 transition-all cursor-pointer whitespace-nowrap w-full sm:w-auto"
                >
                  gtbysheet
                </a>
                <a
                  href={kaspreadsheetUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-8 py-3.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap w-full sm:w-auto"
                >
                  Buy It Now
                </a>
              </div>
              <div className="mt-10">
                <CategoryDropdown />
              </div>
            </div>
          </div>
        </section>

        {/* Spreadsheet System Authority Block - WITH IMAGES */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-xs font-semibold text-accent-600 uppercase tracking-widest mb-3">Supported Systems</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              We Support Multiple Spreadsheet Systems
            </h2>
            <p className="text-foreground-600 text-sm max-w-2xl mx-auto mb-10 leading-relaxed">
              Each system helps you track orders in real time, automate purchasing workflows, reduce manual errors, and improve operational efficiency across all product categories.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {spreadsheetSystems.map((system, idx) => (
                <div key={system.name} className="bg-background-100 rounded-xl overflow-hidden text-left">
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={system.image}
                      alt={system.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        idx === 0 ? 'bg-secondary-100 text-secondary-900' :
                        idx === 1 ? 'bg-accent-100 text-accent-900' :
                        'bg-primary-100 text-primary-800'
                      }`}>
                        {idx === 0 ? 'Recommended' : idx === 1 ? 'Beginner' : 'Advanced'}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">{system.name}</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">{system.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Grid */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 mb-12">
              <div className="lg:w-2/5">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                  All-In-One<br />Shopping Tracker
                </h2>
              </div>
              <div className="lg:w-3/5">
                <p className="text-foreground-600 text-sm leading-relaxed">
                  GTBuy Spreadsheet combines real-time order monitoring, multi-category management, and automated data export into a single powerful system — designed for serious shoppers who want complete control over their purchasing workflow.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {features.map((feat, idx) => (
                <div
                  key={feat.title}
                  className="bg-background-50 rounded-xl p-5 md:p-6"
                >
                  <div className="w-full h-48 md:h-52 rounded-lg overflow-hidden mb-5">
                    <img
                      src={
                        idx === 0
                          ? 'https://readdy.ai/api/search-image?query=Modern%20laptop%20screen%20showing%20colorful%20order%20tracking%20dashboard%20with%20progress%20bars%20status%20indicators%20and%20delivery%20timeline%20warm%20ambient%20office%20lighting%20clean%20minimal%20desk%20with%20notebook%20and%20pen%20editorial%20tech%20photography%20warm%20neutral%20tones%20high%20detail%20organized%20professional&width=800&height=600&seq=feature-track-v2&orientation=landscape'
                          : idx === 1
                          ? 'https://readdy.ai/api/search-image?query=Fashion%20accessories%20and%20apparel%20arranged%20in%20organized%20labeled%20sections%20on%20warm%20cream%20surface%20shoes%20hoodies%20caps%20and%20bags%20in%20neat%20rows%20soft%20diffused%20studio%20lighting%20editorial%20product%20photography%20warm%20neutral%20background%20clean%20structured%20composition%20retail%20display%20aesthetic&width=800&height=600&seq=feature-cats-v2&orientation=landscape'
                          : 'https://readdy.ai/api/search-image?query=Modern%20digital%20tablet%20displaying%20automated%20data%20sync%20interface%20with%20circular%20progress%20animation%20clean%20desk%20setup%20with%20wireless%20keyboard%20warm%20ambient%20window%20lighting%20editorial%20tech%20lifestyle%20photography%20warm%20neutral%20tones%20sophisticated%20productive%20atmosphere&width=800&height=600&seq=feature-sync-v2&orientation=landscape'
                      }
                      alt={feat.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                    <i className={`${feat.icon} text-accent-600 text-lg w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">{feat.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{feat.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
              Compare Spreadsheet Systems
            </h2>
            <div className="overflow-x-auto rounded-xl border border-background-200/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-6 py-4 font-semibold text-foreground-950">System</th>
                    <th className="text-center px-6 py-4 font-semibold text-foreground-950">Ease of Use</th>
                    <th className="text-center px-6 py-4 font-semibold text-foreground-950">Automation</th>
                    <th className="text-center px-6 py-4 font-semibold text-foreground-950">Best For</th>
                  </tr>
                </thead>
                <tbody>
                  {spreadsheetComparison.map((row, idx) => (
                    <tr key={row.system} className={idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                      <td className="px-6 py-4 font-semibold text-foreground-900 whitespace-nowrap">{row.system}</td>
                      <td className="text-center px-6 py-4 text-foreground-600">{row.ease}</td>
                      <td className="text-center px-6 py-4 text-foreground-600">{row.automation}</td>
                      <td className="text-center px-6 py-4 text-foreground-600">{row.bestFor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Product Category Grid */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100" data-product-shop>
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-4 mb-10">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                Shop by<br />Category
              </h2>
              <p className="text-foreground-600 text-sm max-w-md">
                Browse our complete collection across 14 product categories — from shoes and apparel to accessories and more.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat, idx) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className={`group relative rounded-xl overflow-hidden cursor-pointer ${
                    idx === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
                  }`}
                >
                  <div className={`w-full ${idx === 0 ? 'h-56 lg:h-full min-h-[280px]' : 'h-36 md:h-44'}`}>
                    <img
                      src={categoryImages[cat.name] || categoryImages['Other Stuff']}
                      alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                    <span className="text-white text-sm md:text-base font-semibold">{cat.name}</span>
                  </div>
                  {idx === 1 && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-white text-xs font-semibold">Hot</span>
                  )}
                  {idx === 2 && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-primary-500 text-white text-xs font-semibold">New</span>
                  )}
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">
              How It Works
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {howItWorks.map((step) => (
                <div key={step.step} className="text-center">
                  <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                    <span className="font-heading text-xl font-bold text-accent-700">{step.step}</span>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">{step.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 9 SEO Article Preview Cards */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-4 mb-10">
              <div>
                <p className="text-xs font-semibold text-accent-600 uppercase tracking-widest mb-2">Expert Guides</p>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                  Shopping Guides<br />&amp; Tutorials
                </h2>
              </div>
              <Link
                to="/shopping-guide"
                className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
              >
                View All Guides
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {seoArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/shopping-guide/${article.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden hover:bg-background-50/80 transition-colors cursor-pointer"
                >
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.imageAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-xs text-foreground-500">{article.date}</span>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mt-1 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-foreground-600 text-xs leading-relaxed line-clamp-2">
                      {article.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Complete Guide Featured Card */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 items-center">
              <div className="lg:w-2/5">
                <span className="inline-block px-3 py-1 rounded-full border border-foreground-200/60 text-xs font-medium text-foreground-600 mb-4">
                  Featured Guide
                </span>
                <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20workspace%20setup%20with%20laptop%20displaying%20comprehensive%20guide%20document%20on%20screen%20organized%20desk%20with%20plants%20stationery%20and%20warm%20ambient%20lighting%20from%20table%20lamp%20creative%20productive%20atmosphere%20editorial%20tech%20photography%20warm%20neutral%20tones%20high%20detail%20sophisticated%20composition%20inviting%20learning%20environment&width=800&height=600&seq=article-featured-v2&orientation=landscape"
                    alt="GTBuy Spreadsheet Complete Guide"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="lg:w-3/5">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                  The Complete<br />GTBuy Sheet<br />Guide (2026)
                </h2>
                <p className="text-foreground-600 text-sm leading-relaxed mb-6 max-w-lg">
                  Discover how the GTBuy Spreadsheet system transforms your online shopping experience with real-time order tracking, automated workflows, and comprehensive category management. Learn setup tips, compare spreadsheet platforms, and optimize your purchasing strategy with our in-depth guide.
                </p>
                <Link
                  to="/gtbuy-spreadsheet-complete-guide"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  Read Full Article
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Testimonial */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 items-center">
              <div className="lg:w-2/5">
                <div className="w-full h-72 md:h-80 rounded-2xl overflow-hidden bg-accent-100/50">
                  <img
                    src="https://readdy.ai/api/search-image?query=Happy%20satisfied%20customer%20smiling%20while%20browsing%20fashion%20products%20on%20tablet%20in%20bright%20modern%20living%20room%20warm%20afternoon%20natural%20lighting%20genuine%20joyful%20expression%20clean%20contemporary%20interior%20lifestyle%20candid%20photography%20warm%20neutral%20tones%20soft%20bokeh%20background%20authentic%20moment&width=600&height=800&seq=testimonial-v2&orientation=portrait"
                    alt="Satisfied GTBuy Spreadsheet user"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="lg:w-3/5">
                <span className="text-xs font-medium text-foreground-500 tracking-wide mb-3 block">(Verified Guide)</span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold mb-8 leading-tight">
                  <span className="text-foreground-950">Trusted by</span>{' '}
                  <span className="text-foreground-400">2,000+ Buyers</span>
                </h2>
                <blockquote className="text-foreground-700 text-base md:text-lg leading-relaxed italic mb-6 border-l-2 border-accent-400 pl-5">
                  &ldquo;GTBuy Spreadsheet completely changed how I manage my online purchases. The category tracking system saves me hours every week, and the automated order monitoring means I never miss a delivery update. It is the most efficient shopping workflow tool I have ever used.&rdquo;
                </blockquote>
                <p className="text-foreground-950 font-semibold text-sm">&mdash; Marcus R., E-commerce Operator</p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Safety Block */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
              Your Privacy Matters
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-foreground-600">
              <div className="p-5 rounded-xl bg-background-100">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-shield-check-line text-accent-600 text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="font-semibold text-foreground-900 mb-1">No Hidden Data</p>
                <p className="text-xs">Transparent data practices</p>
              </div>
              <div className="p-5 rounded-xl bg-background-100">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-lock-line text-accent-600 text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="font-semibold text-foreground-900 mb-1">User Controlled</p>
                <p className="text-xs">Full spreadsheet access control</p>
              </div>
              <div className="p-5 rounded-xl bg-background-100">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-eye-off-line text-accent-600 text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="font-semibold text-foreground-900 mb-1">Secure Browsing</p>
                <p className="text-xs">Encrypted connections</p>
              </div>
              <div className="p-5 rounded-xl bg-background-100">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-file-shield-line text-accent-600 text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="font-semibold text-foreground-900 mb-1">No Data Sharing</p>
                <p className="text-xs">Your info stays private</p>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-16 md:py-24 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              Start Optimizing Your Spreadsheet Workflow Today
            </h2>
            <p className="text-foreground-600 text-sm mb-8 max-w-xl mx-auto">
              Access the full product catalog and start tracking your orders with the most efficient spreadsheet system available.
            </p>
            <a
              href={kaspreadsheetUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              Access GTBuy Spreadsheet System
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
          {/* Product banner strip */}
          <div className="max-w-5xl mx-auto mt-14 flex flex-wrap items-center justify-center gap-4 md:gap-8">
            {bannerProducts.map((product) => (
              <div key={product.name} className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-background-50">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}